import { Injectable } from '@angular/core';
import { initializePaddle, Paddle, PaddleEventData, Environments, CheckoutCustomer } from '@paddle/paddle-js';
import { LoggingService } from 'services/logging.service';
import { TrialPeriod } from 'pages/subscribe/checkout-trial';
import { kPricePreviewNetworkErrorReport, previewWithCountryFallbackAsync } from 'services/paddle-price-preview-failure';

/** How the most recent price preview in this session ended. */
export enum PricePreviewOutcome {
  /** No price preview was asked for in this session. */
  NotAsked = 'not-asked',
  /** The preview never reached Paddle (a network failure). */
  NetworkFailed = 'network-failed',
  /** The preview did not fail for the network: it succeeded, or failed some other way. */
  Other = 'other',
}

/** A localized price for one plan, derived from Paddle's PricePreview. */
export interface PaddlePlanPrice {
  /** The Paddle price id (`pri_…`). */
  priceId: string;
  /** Localized, currency-formatted total (e.g. "$20.00"). */
  formattedTotal: string;
  /** The same total in the currency's smallest unit (e.g. 2000 for $20.00), from Paddle's
   *  `totals.total`. Undefined when Paddle sent something that is not a whole number. The
   *  Yearly plan's saving is worked out from this, never from the text (#6145). */
  totalMinor: number | undefined;
  /** The free trial this price carries (`price.trialPeriod`), or undefined for none. The
   *  Subscribe screen's trial wording is read from this (#6145). */
  trialPeriod: TrialPeriod | undefined;
  /** ISO currency code Paddle resolved for the buyer. */
  currency: string;
}

// Paddle sends money amounts as strings of whole minor units ("2299" for 22.99)
function parseMinorUnits(text: string | undefined): number | undefined {
  if (!text || !/^\d+$/.test(text)) return undefined;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : undefined;
}

/** What the subscribe page needs to open a Paddle inline checkout. */
export interface PaddleOpenCheckoutOptions {
  /** The plan's Paddle price id. */
  priceId: string;
  /** CSS selector of the container the inline frame mounts into. */
  frameTarget: string;
  /** How many units of the price to buy. Defaults to 1. The coach per-seat
   *  coaching checkout (Phase 4) starts at 1 (the coach's own seat). */
  quantity?: number;
  /** `custom_data` stamped onto the checkout (origin_host, checkout_id,
   *  app_user_id?, is_e2e?). Round-trips to the webhook. */
  customData: Record<string, unknown>;
  /** Pre-fill + lock the email for a logged-in buyer (with allowLogout=false).
   *  Ignored when `customerId` or `customerAuthToken` is set (Paddle wants one
   *  customer handle, not several). */
  customerEmail?: string;
  /** Existing Paddle customer id (`ctm_…`) for a returning customer. When set (and
   *  no `customerAuthToken`), the checkout opens with `customer: { id }` — plus
   *  `address: { id }` when `addressId` is also set — instead of an email, so the
   *  subscription attaches to the buyer's existing Paddle customer record (no
   *  duplicate customer). Takes precedence over `customerEmail`. */
  customerId?: string;
  /** Saved Paddle billing-address id (`add_…`) for a returning customer (#4097).
   *  When set alongside `customerId` (and no `customerAuthToken`), it is passed as
   *  `customer.address.id` so Paddle skips its "enter your details" page. */
  addressId?: string;
  /** Short-lived Paddle `customerAuthToken` for a returning customer (#4097). When
   *  set it takes precedence over `customerId` / `customerEmail`: the checkout opens
   *  with `customerAuthToken` and NO `customer` field (Paddle.js makes the two
   *  mutually exclusive). The token authenticates the returning customer, so Paddle
   *  presents their saved address (skips the details page) AND their saved card
   *  (when they opted in on a prior purchase). Treated as a credential: never logged
   *  or persisted on the client. */
  customerAuthToken?: string;
  /** When false, the email field is locked (logged-in case). */
  allowLogout: boolean;
  /** Where Paddle redirects on completion (entitlement still comes from the webhook). */
  successUrl?: string;
  /** The promo-campaign discount to open the checkout with (#6965). When set, the
   *  buyer cannot remove it, since without it they would pay full price on a price
   *  with no trial. */
  discountId?: string;
}

/**
 * Thin wrapper around `@paddle/paddle-js` for the inline web checkout (#3861,
 * Step 7). The subscribe page owns the lifecycle: it inits the SDK with the
 * server-resolved environment + client token and an event callback, previews
 * the two plan prices for the selector, opens the inline checkout for the
 * chosen plan, and closes it on teardown or on an email conflict.
 *
 * This is the Paddle analogue of the PayPro iframe path; the two render paths
 * live side by side on the subscribe page, picked by the provider the server
 * returned. See design-docs/paddle-integration.md decision 7.
 */
@Injectable({ providedIn: 'root' })
export class PaddleCheckoutService {
  private paddle?: Paddle;

  /**
   * How this session's most recent price preview ended. A checkout failure that
   * arrives with no fields reports it, so the next such event says whether
   * Paddle's API host was already unreachable (df-38f32a018053).
   */
  lastPricePreview = PricePreviewOutcome.NotAsked;

  constructor(private logging: LoggingService) {}

  /** True once `initAsync` has successfully created a Paddle instance. */
  get isInitialized(): boolean {
    return !!this.paddle;
  }

  /**
   * Initialize Paddle.js with the server-resolved environment + client token.
   * The `eventCallback` receives every checkout event (we use
   * `checkout.customer.updated` for the email-conflict check). Throws if Paddle
   * fails to load so the caller can surface a checkout-unavailable state rather
   * than silently rendering an empty frame.
   */
  async initAsync(opts: { environment: 'sandbox' | 'live'; token: string; eventCallback: (event: PaddleEventData) => void }): Promise<void> {
    // map our two-value env onto Paddle's ('live' → 'production').
    const environment: Environments = opts.environment === 'live' ? 'production' : 'sandbox';
    // our app is always dark mode, so set the Paddle checkout's default theme to
    // dark here. Paddle merges these init-level checkout settings with the
    // per-open() settings field by field, and openInlineCheckout() does not set a
    // theme, so this dark default applies to the inline checkout. without it
    // Paddle renders its checkout in light mode and clashes with our dark UI.
    const instance = await initializePaddle({
      environment,
      token: opts.token,
      eventCallback: opts.eventCallback,
      checkout: { settings: { theme: 'dark' } },
    });
    if (!instance) {
      throw new Error('Paddle.js failed to initialize');
    }
    this.paddle = instance;
  }

  /**
   * Fetch localized prices for the given plan price ids, so the plan selector
   * can show real prices before the inline frame loads. Returns an empty array
   * on failure (the selector then shows plan titles without a price, and the
   * inline frame still shows the authoritative price). `countryCode` lets Paddle
   * localize before we know the buyer's address; when Paddle refuses that
   * country, the preview is asked again without it (#5445).
   *
   * With `discountId` (#6965) the totals are the sale prices: Paddle takes one
   * discount per preview request, so the caller asks once per discount, naming only
   * the prices that discount applies to.
   */
  async previewPricesAsync(priceIds: string[], countryCode?: string, discountId?: string): Promise<PaddlePlanPrice[]> {
    const paddle = this.paddle;
    if (!paddle) {
      throw new Error('Paddle not initialized');
    }
    // a price-preview failure is non-fatal: the helper reports it at the right
    // tier and the caller falls back to title-only plans. the response is mapped
    // INSIDE the guarded call, so a malformed success (Paddle.js hands back
    // `lineItems: undefined` when `line_items` is null) is reported too instead
    // of throwing past the picker and leaving it with no plans at all.
    let previewOutcome = PricePreviewOutcome.Other;
    const prices = await previewWithCountryFallbackAsync(
      async (cc) => {
        const resp = await paddle.PricePreview({
          items: priceIds.map(priceId => ({ priceId, quantity: 1 })),
          ...(cc ? { address: { countryCode: cc } } : {}),
          ...(discountId ? { discountId } : {}),
        });
        const currency = resp.data.currencyCode;
        return resp.data.details.lineItems.map(item => ({
          priceId: item.price.id,
          formattedTotal: item.formattedTotals.total,
          totalMinor: parseMinorUnits(item.totals?.total),
          trialPeriod: item.price?.trialPeriod ?? undefined,
          currency,
        }));
      },
      countryCode,
      {
        info: (name, context) => {
          // remember a network failure: a later fieldless checkout failure reports it
          if (name === kPricePreviewNetworkErrorReport) {
            previewOutcome = PricePreviewOutcome.NetworkFailed;
          }
          this.logging.trackSilentInfo(name, context);
        },
        problem: (name, context) => this.logging.trackSilentProblem(name, context),
      },
    );
    this.lastPricePreview = previewOutcome;
    return prices ?? [];
  }

  /** Open the inline checkout for one plan inside `frameTarget`. */
  openInlineCheckout(opts: PaddleOpenCheckoutOptions): void {
    if (!this.paddle) {
      throw new Error('Paddle not initialized');
    }
    // the shared, identity-agnostic part of the checkout options. The identity
    // handle (customer vs customerAuthToken) is added per-branch below.
    const baseOptions = {
      items: [{ priceId: opts.priceId, quantity: opts.quantity ?? 1 }],
      customData: opts.customData,
      ...(opts.discountId ? { discountId: opts.discountId } : {}),
      settings: {
        displayMode: 'inline' as const,
        frameTarget: opts.frameTarget,
        allowLogout: opts.allowLogout,
        // #6965: hide Paddle's own "add discount" box. A code typed there would bypass
        // our server, which is what swaps in the no-trial price a sale needs, so the
        // only way to enter a code is the page's own field.
        showAddDiscounts: false,
        ...(opts.discountId ? { allowDiscountRemoval: false } : {}),
        // Paddle's recommended inline layout: a transparent, borderless,
        // full-width frame with an initial height. Without `frameStyle` Paddle's
        // injected iframe uses defaults that escape the container and overlap our
        // surrounding card UI. See developer.paddle.com inline-checkout guide.
        // (The Firefox white-card fix lives in subscribe.scss, not here — it
        // forces the iframe's color-scheme to match Paddle's so the canvas stays
        // transparent and our dark page shows through.)
        frameInitialHeight: 450,
        frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
        ...(opts.successUrl ? { successUrl: opts.successUrl } : {}),
      },
    };

    // pick the customer handle for a returning-or-fresh buyer (#4097). Paddle.js
    // makes `customerAuthToken` and `customer` MUTUALLY EXCLUSIVE (each forbids the
    // other in its type), so we branch into two separate `open` calls rather than
    // build one object carrying both keys — TypeScript cannot narrow the union when
    // both keys are present. Precedence:
    //   1. customerAuthToken (richest) → authenticates the returning customer, so
    //      Paddle presents their saved address (skips details) AND saved card.
    //   2. else customerId → attach to their existing customer record; also pass
    //      the saved address id (when known) to skip the details page.
    //   3. else customerEmail → a first-time buyer types their details fresh.
    if (opts.customerAuthToken) {
      this.paddle.Checkout.open({ ...baseOptions, customerAuthToken: opts.customerAuthToken });
      return;
    }
    // `customer` is Paddle's id-XOR-email union, with an optional nested address.
    const customer: CheckoutCustomer | undefined = opts.customerId
      ? { id: opts.customerId, ...(opts.addressId ? { address: { id: opts.addressId } } : {}) }
      : (opts.customerEmail ? { email: opts.customerEmail } : undefined);
    this.paddle.Checkout.open({ ...baseOptions, ...(customer ? { customer } : {}) });
  }

  /**
   * Swap the plan on an ALREADY-OPEN inline checkout (a plan-radio switch).
   * Uses Paddle's `updateCheckout` rather than re-`open`-ing, which would not be
   * the documented way to change items and can leave a stale frame.
   *
   * `updateCheckout` rather than `updateItems` (#6965): the discount and the custom
   * data belong to the plan, so they must move with it. A switch to a plan outside
   * the campaign passes `discountId: null`, which removes the other plan's discount.
   */
  updateCheckout(opts: { priceId: string; discountId: string | undefined; customData: Record<string, unknown> }): void {
    if (!this.paddle) {
      throw new Error('Paddle not initialized');
    }
    this.paddle.Checkout.updateCheckout({
      items: [{ priceId: opts.priceId, quantity: 1 }],
      discountId: opts.discountId ?? null,
      customData: opts.customData,
    });
  }

  /** Tear down the open checkout (teardown, or an email-conflict abort). */
  close(): void {
    try {
      this.paddle?.Checkout.close();
    } catch {
      // expected: the checkout may already be closed/torn down; nothing to do.
    }
  }
}
